import { memo, useMemo } from 'react';
import { hasKey, useTranslation } from '@betty/i18n';
import { useNavigation, StackNavigationProp } from '@betty/navigation';
import { useDispatch } from '@betty/toolkit/state';
import { formatAmount } from '@betty/toolkit/utils';
import { Box, Button, InfoImage } from '@betty/moonlight';
import { openChat } from '@betty/chat/state';
import { getStatusConfig } from './PaymentStatusFailed.utils';
import {
  ButtonActions,
  PaymentStatusFailedProps,
} from './PaymentStatusFailed.types';
import PaymentMethodVerificationRequired from '../PaymentStatusStarted/PaymentMethodVerificationRequired/PaymentMethodVerificationRequired';
import PaymentMethodVerificationPending from '../PaymentStatusStarted/PaymentMethodVerificationPending/PaymentMethodVerificationPending';

function PaymentStatusFailed(props: PaymentStatusFailedProps) {
  const { amount, messageCode = 'try-again', transactionType } = props;
  const { t } = useTranslation('wallet');
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const dispatch = useDispatch();
  const type = transactionType.toLowerCase() as Lowercase<
    typeof transactionType
  >;
  const placeholders = { amount: formatAmount(amount), context: type };
  const { primaryAction, secondaryAction, ...restConfig } =
    getStatusConfig(type)[messageCode];
  const actions = useMemo<Record<ButtonActions, () => void>>(
    () => ({
      goBack: () => navigation.goBack(),
      openChat: () => dispatch(openChat()),
      close: () => navigation.getParent()?.goBack(),
    }),
    [navigation, dispatch],
  );

  if (messageCode === 'additional-payment-method-verification-required') {
    return <PaymentMethodVerificationRequired {...props} />;
  }

  if (messageCode === 'payment-method-document-pending-review') {
    return <PaymentMethodVerificationPending {...props} />;
  }

  return (
    <InfoImage
      flex={1}
      image="common.rejected"
      title={
        hasKey(`wallet:paymentStatusFailedTitle_${messageCode}`) ||
        hasKey(`wallet:paymentStatusFailedTitle_${messageCode}_${type}`)
          ? t(`paymentStatusFailedTitle_${messageCode}`, placeholders)
          : t('paymentStatusFailedTitle')
      }
      description={t(
        `paymentStatusFailedDescription_${messageCode}`,
        placeholders,
      )}
      {...restConfig}
    >
      <Box flex={1} justifyContent="flex-end" gap="space-400">
        {primaryAction && (
          <Button
            text={t(
              `paymentStatusFailedPrimaryButton_${messageCode}`,
              placeholders,
            )}
            size="large"
            width="100%"
            onPress={actions[primaryAction]}
          />
        )}
        {secondaryAction && (
          <Button
            variant="secondary"
            text={t(
              `paymentStatusFailedSecondaryButton_${messageCode}`,
              placeholders,
            )}
            size="large"
            width="100%"
            onPress={actions[secondaryAction]}
          />
        )}
      </Box>
    </InfoImage>
  );
}

export default memo(PaymentStatusFailed);
